import moment from 'moment';
import {
    parsePermissionOverrideToForm,
    readPersonalPermissionEnabled,
} from './job-settings-personal-permission.utils';
import type { JobSettingsEmployeeInfo, JobSettingsEmployeeRow } from './job-settings.types';

const AU_LOGIN_CORE_KEYS = ['CWA_login', 'COP_login', 'MOP_login', 'RWA_login', 'KWA_login'] as const;

/** 与 RBP `staff-setting-detail` `onChangeCheckbox` 中 `job_rates` 派生规则一致 */
export function computeJobRatesFromRoleFlags(isReturns: number, isTimecard: number, isOther: number): number {
    const r = Number(isReturns) === 1;
    const t = Number(isTimecard) === 1;
    const o = Number(isOther) === 1;
    if (!r && !t && !o) {
        return 0;
    }
    if (r && !t && !o) {
        return 1;
    }
    if (!r && t && !o) {
        return 2;
    }
    if (r && t && !o) {
        return 3;
    }
    if (!r && !t && o) {
        return 4;
    }
    if (r && !t && o) {
        return 5;
    }
    if (!r && t && o) {
        return 6;
    }
    if (r && t && o) {
        return 7;
    }
    return 0;
}

/**
 * 由 `job_rates` 反推三项角色勾选（与 RBP `staff-setting.tsx` 查看详情时对 `detail` 的赋值一致）。
 * `job_rates` 为 0～7 时与 `computeJobRatesFromRoleFlags` 互为逆映射。
 */
export function roleFlagsFromJobRates(jobRates: number): {
    isReturns: number;
    isTimecard: number;
    isOther: number;
} {
    const j = Math.trunc(jobRates);
    switch (j) {
        case 1:
            return { isReturns: 1, isTimecard: 0, isOther: 0 };
        case 2:
            return { isReturns: 0, isTimecard: 1, isOther: 0 };
        case 3:
            return { isReturns: 1, isTimecard: 1, isOther: 0 };
        case 4:
            return { isReturns: 0, isTimecard: 0, isOther: 1 };
        case 5:
            return { isReturns: 1, isTimecard: 0, isOther: 1 };
        case 6:
            return { isReturns: 0, isTimecard: 1, isOther: 1 };
        case 7:
            return { isReturns: 1, isTimecard: 1, isOther: 1 };
        default:
            return { isReturns: 0, isTimecard: 0, isOther: 0 };
    }
}

function readNum01(v: unknown): number {
    if (v == null || v === '') {
        return 0;
    }
    const n = Number(v);
    if (!Number.isFinite(n)) {
        return 0;
    }
    return n !== 0 ? 1 : 0;
}

export function formatEmployeeEndDate(raw: unknown): string {
    if (raw == null || raw === '') {
        return '';
    }
    const m = moment(raw as string | Date);
    return m.isValid() ? m.format('YYYY-MM-DD') : String(raw);
}

/**
 * 与 RBP `staff-setting-detail` 的 `patch_employee_information` 一致：去掉不参与 PATCH 的字段。
 */
export function stripEmployeePatchPayload(merged: Record<string, unknown>): Record<string, unknown> {
    // 与 RBP 一致：PATCH 正文中不含 employee_id 及三项 job 勾选派生字段
    const { employee_id, employee_class_name, is_returns, is_timecard, is_other, ...data } = merged;
    void employee_id;
    void employee_class_name;
    void is_returns;
    void is_timecard;
    void is_other;
    /** 禁止 PATCH 透传 GET 返回的 `permission_override: null` / `''`（由 `applyPersonalPermissionPatchToBody` 按需写入） */
    if (data.permission_override == null || data.permission_override === '') {
        delete data.permission_override;
    }
    return data;
}

/**
 * 新建员工 POST 正文：`jobSettingsFormToApiPatch` 加上云 Shell 创建占位员工沿用的 `end_date`。
 */
export function jobSettingsFormToApiPost(
    info: JobSettingsEmployeeInfo,
    employeeClassIdStr: string,
    includeAuLoginFields: boolean,
): Record<string, unknown> {
    return {
        ...jobSettingsFormToApiPatch(info, employeeClassIdStr, includeAuLoginFields),
        end_date: moment().format('YYYY-MM-DD HH:mm:ss'),
    };
}

export function jobSettingsFormToApiPatch(
    info: JobSettingsEmployeeInfo,
    employeeClassIdStr: string,
    includeAuLoginFields: boolean,
): Record<string, unknown> {
    const pc = info.pcApsId.trim();
    const comm = Number(info.commission);
    const jobRates = computeJobRatesFromRoleFlags(info.isReturns, info.isTimecard, info.isOther);
    const base: Record<string, unknown> = {
        employee_last_name: info.employeeLastName,
        check_name: info.checkName,
        name_suffix: info.nameSuffix,
        ssn_sin: info.ssnSin,
        password_id: info.passwordId,
        pc_aps_id: pc === '' ? null : Number(pc) || pc,
        commission: Number.isFinite(comm) ? comm : 0,
        employee_class: Number(employeeClassIdStr) || 0,
        job_rates: jobRates,
    };
    if (includeAuLoginFields) {
        base.CWA_login = info.cwaLogin;
        base.COP_login = info.copLogin;
        base.MOP_login = info.mopLogin;
        base.RWA_login = info.rwaLogin;
        base.KWA_login = info.kwaLogin;
        base.Ecco_POS_login = info.eccoPosLogin;
    }
    return base;
}

export function buildClassNameByIdMap(
    classes: Array<{ employee_class_id?: number; employee_class_name?: string }>,
): Map<number, string> {
    const m = new Map<number, string>();
    for (const c of classes) {
        const id = c.employee_class_id;
        if (id != null) {
            m.set(id, c.employee_class_name ?? '');
        }
    }
    return m;
}

export function apiEmployeeToRow(
    emp: Record<string, unknown>,
    classById: Map<number, string>,
): { row: JobSettingsEmployeeRow; raw: Record<string, unknown> } {
    const eid = Number(emp.employee_id);
    const rawClass = emp.employee_class;
    /** 有效员工等级 id；null / 0 / 非法值视为未分配岗位 */
    let classId = 0;
    if (rawClass != null && rawClass !== '') {
        const parsed = Number(rawClass);
        if (Number.isFinite(parsed) && parsed > 0) {
            classId = Math.trunc(parsed);
        }
    }
    const name = String(emp.employee_last_name ?? '');
    const roleName =
        classById.get(classId) || String(emp.employee_class_name ?? '');

    const jobRatesRaw = Number(emp.job_rates);
    let isReturns: number;
    let isTimecard: number;
    let isOther: number;
    let jobRates: number;

    if (Number.isFinite(jobRatesRaw) && jobRatesRaw >= 0 && jobRatesRaw <= 7) {
        jobRates = Math.trunc(jobRatesRaw);
        const fromJr = roleFlagsFromJobRates(jobRates);
        isReturns = fromJr.isReturns;
        isTimecard = fromJr.isTimecard;
        isOther = fromJr.isOther;
    } else {
        isReturns = readNum01(emp.is_returns);
        isTimecard = readNum01(emp.is_timecard);
        isOther = readNum01(emp.is_other);
        jobRates = computeJobRatesFromRoleFlags(isReturns, isTimecard, isOther);
    }

    const auLoginFieldsPatchable = AU_LOGIN_CORE_KEYS.every((k) => typeof emp[k] === 'number');

    const info: JobSettingsEmployeeInfo = {
        employeeLastName: name,
        checkName: String(emp.check_name ?? ''),
        nameSuffix: String(emp.name_suffix ?? ''),
        ssnSin: String(emp.ssn_sin ?? ''),
        passwordId: String(emp.password_id ?? ''),
        pcApsId: emp.pc_aps_id != null && `${emp.pc_aps_id}` !== '' ? String(emp.pc_aps_id) : '',
        commission: String(emp.commission ?? '0'),
        endDate: formatEmployeeEndDate(emp.end_date),
        cwaLogin: readNum01(emp.CWA_login),
        copLogin: readNum01(emp.COP_login),
        mopLogin: readNum01(emp.MOP_login),
        rwaLogin: readNum01(emp.RWA_login),
        kwaLogin: readNum01(emp.KWA_login),
        eccoPosLogin: typeof emp.Ecco_POS_login === 'number' ? readNum01(emp.Ecco_POS_login) : 0,
        isReturns,
        isTimecard,
        isOther,
        jobRates,
    };

    const raw = { ...emp };

    return {
        raw,
        row: {
            id: String(eid),
            employeeId: String(eid),
            displayName: name,
            roleDisplayName: roleName,
            employeeClassId: classId,
            defaultRoleValue: classId > 0 ? String(classId) : '',
            defaultPersonalSupplement: readPersonalPermissionEnabled(emp.personal_permission_enabled),
            defaultPersonalPermissionOverride: readPersonalPermissionEnabled(emp.personal_permission_enabled)
                ? parsePermissionOverrideToForm(emp.permission_override)
                : null,
            employeeInfoDefaults: info,
            auLoginFieldsPatchable,
            /** 与业务约定一致：创建员工时间取接口 `end_date`，仅展示年月日（与 `endDate` 格式化一致） */
            employeeCreatedAtDisplay: formatEmployeeEndDate(emp.end_date),
        },
    };
}
